import { collectLiveFieldOverrides, isTextLikeFormElementType } from '@/common/utils/liveFormFieldOverrides'
import { mergePageDataFieldSnapshot } from '@/common/utils/resolveClientDataActualFields'
import { resolveElementRegistryFormId } from '@/common/utils/resolveElementRegistryFormId'
import { emptyUniDataTableField } from '@/common/utils/treeTableReloadNodePayload'
import { setTypes } from '@/common/utils/typedValueSerializer'

const TABLE_LIKE_ELEMENT_TYPES = new Set(['TABLE', 'TREETABLE', 'EDITABLE', 'GROUPTABLE'])

/** Empty payload mirroring useElement.empty (text / table / FILEATTACHMENTS / other → null). */
export function resolveEmptyFieldValue(elementType: string | undefined | null): unknown {
  const elType = String(elementType ?? '').toUpperCase()
  if (isTextLikeFormElementType(elType)) {
    return { type: 'String', value: '' }
  }
  if (TABLE_LIKE_ELEMENT_TYPES.has(elType) || elType === 'FILEATTACHMENTS') {
    return emptyUniDataTableField()
  }
  return null
}

export interface PlatformTestSlotOptions {
  slotId?: string
}

export interface PlatformTestSetFieldOptions extends PlatformTestSlotOptions {
  refill?: boolean
}

export interface PlatformTestElementRef {
  fieldName: string
  formId: string
  panelId: string
  elementId: string
}

export interface PlatformTestApiResult {
  ok: boolean
  error?: string
  fieldName?: string
  slotId?: string
  value?: unknown
}

export interface PlatformTestApiDeps {
  getActiveSlot: () => string
  getElements: () => Record<string, any>
  getForms: () => Record<string, Record<string, Record<string, Record<string, unknown>>>>
  getPagesData: () => Record<string, any>
  setValue: (params: {
    value: Record<string, any>
    formId: string | number
    panelId: string | number
    elementId: string | number
  }) => void
  refillElement: (elementName: string, fieldData: any, formId?: string) => void
}

export function resolveRefillFormId(
  rawFormId: string,
  forms: Record<string, unknown>,
  elements: Record<string, any>,
  pagesData: Record<string, any>,
): string {
  const fid = String(rawFormId ?? '').trim()
  if (!fid) return fid
  if (forms[fid] != null) return fid

  const activeRegistryFormIds = new Set<string>()
  for (const el of Object.values(elements) as any[]) {
    const ef = String(el?.formId ?? '').trim()
    if (ef) activeRegistryFormIds.add(ef)
  }
  if (activeRegistryFormIds.has(fid)) return fid

  for (const [slotId, pd] of Object.entries(pagesData)) {
    if (!pd || typeof pd !== 'object') continue
    const slot = String(slotId ?? '').trim()
    const registryId = resolveElementRegistryFormId(pd, slot)
    const aliases = [
      slot,
      registryId,
      pd?.jsResult?.form_id,
      pd?.form?.form?._attributes?.id,
      pd?.form?._attributes?.id,
      pd?.formId,
    ]
      .map((v: unknown) => String(v ?? '').trim())
      .filter(Boolean)
    if (!aliases.includes(fid)) continue

    const candidates = [registryId, slot].map((v) => String(v ?? '').trim()).filter(Boolean)
    const hit = candidates.find((c) => forms[c] != null || activeRegistryFormIds.has(c))
    if (hit) return hit
    return candidates[0] || fid
  }

  return fid
}

export function resolveRegisteredElement(
  fieldName: string,
  slotId: string,
  elements: Record<string, any>,
  forms: Record<string, unknown>,
  pagesData: Record<string, any>,
): PlatformTestElementRef | null {
  const trimmedName = String(fieldName ?? '').trim()
  if (!trimmedName || !slotId) return null

  const pageData = pagesData[slotId]
  const registryFormId = resolveElementRegistryFormId(pageData, slotId)
  const curFid = resolveRefillFormId(registryFormId || slotId, forms, elements, pagesData)

  let element = elements[trimmedName] as any
  if (element && curFid && String(element.formId ?? '') !== curFid) {
    const match = Object.entries(elements).find(
      ([name, el]) => name === trimmedName && String((el as any).formId ?? '') === curFid,
    )
    element = match ? (match[1] as any) : undefined
  }

  if (!element && trimmedName) {
    const withRefill = Object.entries(elements).filter(
      ([name, el]) => name === trimmedName && typeof (el as any)?.refill === 'function',
    )
    if (withRefill.length === 1) {
      element = withRefill[0][1] as any
    } else if (withRefill.length > 1 && curFid) {
      const activeFormIds = new Set(Object.keys(forms))
      const pick = withRefill.find(([, el]) => activeFormIds.has(String((el as any).formId ?? '')))
      if (pick) element = pick[1] as any
    }
  }

  if (!element) return null

  const formId = String(element.formId ?? curFid ?? '').trim()
  const panelId = String(element.panelId ?? '').trim()
  const elementId = String(element.elementId ?? '').trim()
  if (!formId || !panelId || !elementId) return null

  return { fieldName: trimmedName, formId, panelId, elementId }
}

export function toPlainFieldValue(field: unknown): unknown {
  if (field == null) return null
  if (typeof field !== 'object' || Array.isArray(field)) return field
  if ('value' in field) {
    const value = (field as { value: unknown }).value
    if (value != null && typeof value === 'object') {
      try {
        return JSON.parse(JSON.stringify(value))
      } catch {
        return value
      }
    }
    return value
  }
  return field
}

export function toTypedFieldValue(value: unknown): { type: string; value: unknown } {
  if (
    value != null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    'value' in value &&
    'type' in value
  ) {
    return value as { type: string; value: unknown }
  }
  return setTypes({ field: value }).field as { type: string; value: unknown }
}

function resolveSlotId(deps: PlatformTestApiDeps, options?: PlatformTestSlotOptions): string {
  const explicit = String(options?.slotId ?? '').trim()
  if (explicit) return explicit
  return String(deps.getActiveSlot() ?? '').trim()
}

function readRegistryField(
  forms: Record<string, Record<string, Record<string, Record<string, unknown>>>>,
  ref: PlatformTestElementRef,
): unknown {
  return forms[ref.formId]?.[ref.panelId]?.[ref.elementId]?.[ref.fieldName]
}

function collectFieldNamesForSlot(
  slotId: string,
  elements: Record<string, any>,
  pagesData: Record<string, any>,
  forms: Record<string, unknown>,
): string[] {
  const registryFormId = resolveElementRegistryFormId(pagesData[slotId], slotId)
  const curFid = resolveRefillFormId(registryFormId || slotId, forms, elements, pagesData)
  const names = new Set<string>()

  for (const [name, el] of Object.entries(elements)) {
    if (!name) continue
    const elFormId = String((el as any)?.formId ?? '').trim()
    if (curFid && elFormId && elFormId !== curFid) continue
    names.add(name)
  }

  const snapshot = mergePageDataFieldSnapshot(pagesData[slotId])
  for (const key of Object.keys(snapshot)) {
    if (key.startsWith('$') || key === 'fields' || key === 'ProcessInstances') continue
    names.add(key)
  }

  return [...names].sort()
}

export function createPlatformTestApi(deps: PlatformTestApiDeps) {
  const readFieldSources = (fieldName: string, slotId: string) => {
    const elements = deps.getElements()
    const forms = deps.getForms()
    const pagesData = deps.getPagesData()
    const pageData = pagesData[slotId]
    const ref = resolveRegisteredElement(fieldName, slotId, elements, forms, pagesData)

    const registryFormId = resolveElementRegistryFormId(pageData, slotId)
    const formIdForLive = ref?.formId ?? resolveRefillFormId(registryFormId || slotId, forms, elements, pagesData)
    const liveOverrides = collectLiveFieldOverrides(formIdForLive, elements)
    const snapshot = mergePageDataFieldSnapshot(pageData)
    const registryField = ref ? readRegistryField(forms, ref) : undefined

    return { ref, liveOverrides, snapshot, registryField, slotId }
  }

  const resolveFieldValue = (fieldName: string, options?: PlatformTestSlotOptions): PlatformTestApiResult => {
    const slotId = resolveSlotId(deps, options)
    if (!slotId) {
      return { ok: false, error: 'NO_ACTIVE_SLOT', fieldName }
    }

    const { ref, liveOverrides, snapshot, registryField } = readFieldSources(fieldName, slotId)
    const trimmedName = String(fieldName ?? '').trim()

    if (liveOverrides[trimmedName] !== undefined) {
      return { ok: true, value: toPlainFieldValue(liveOverrides[trimmedName]), fieldName: trimmedName, slotId }
    }

    if (snapshot[trimmedName] !== undefined) {
      return { ok: true, value: toPlainFieldValue(snapshot[trimmedName]), fieldName: trimmedName, slotId }
    }

    if (registryField !== undefined) {
      return { ok: true, value: toPlainFieldValue(registryField), fieldName: trimmedName, slotId }
    }

    if (!ref) {
      return { ok: false, error: 'FIELD_NOT_FOUND', fieldName: trimmedName, slotId }
    }

    return { ok: true, value: null, fieldName: trimmedName, slotId }
  }

  return {
    isReady(): boolean {
      const slotId = String(deps.getActiveSlot() ?? '').trim()
      if (!slotId) return false
      const pageData = deps.getPagesData()[slotId]
      return pageData != null && typeof pageData === 'object'
    },

    getActiveSlot(): string {
      return String(deps.getActiveSlot() ?? '').trim()
    },

    listFields(options?: PlatformTestSlotOptions): PlatformTestApiResult & { fields?: string[] } {
      const slotId = resolveSlotId(deps, options)
      if (!slotId) {
        return { ok: false, error: 'NO_ACTIVE_SLOT' }
      }
      return {
        ok: true,
        slotId,
        fields: collectFieldNamesForSlot(
          slotId,
          deps.getElements(),
          deps.getPagesData(),
          deps.getForms(),
        ),
      }
    },

    getField(fieldName: string, options?: PlatformTestSlotOptions): unknown {
      const result = resolveFieldValue(fieldName, options)
      if (!result.ok) return null
      return result.value ?? null
    },

    getFieldTyped(fieldName: string, options?: PlatformTestSlotOptions): PlatformTestApiResult {
      const plain = resolveFieldValue(fieldName, options)
      if (!plain.ok) return plain

      const typed = toTypedFieldValue(plain.value)
      return { ok: true, value: typed, fieldName: plain.fieldName, slotId: plain.slotId }
    },

    getFields(options?: PlatformTestSlotOptions): PlatformTestApiResult & { fields?: Record<string, unknown> } {
      const listed = this.listFields(options)
      if (!listed.ok || !listed.fields) {
        return { ok: false, error: listed.error ?? 'LIST_FIELDS_FAILED', slotId: listed.slotId }
      }

      const out: Record<string, unknown> = {}
      for (const name of listed.fields) {
        const result = resolveFieldValue(name, { slotId: listed.slotId })
        if (result.ok) {
          out[name] = result.value ?? null
        }
      }
      return { ok: true, slotId: listed.slotId, fields: out }
    },

    setField(
      fieldName: string,
      value: unknown,
      options?: PlatformTestSetFieldOptions,
    ): PlatformTestApiResult {
      const slotId = resolveSlotId(deps, options)
      const trimmedName = String(fieldName ?? '').trim()
      if (!slotId) {
        return { ok: false, error: 'NO_ACTIVE_SLOT', fieldName: trimmedName }
      }

      const elements = deps.getElements()
      const forms = deps.getForms()
      const pagesData = deps.getPagesData()
      const ref = resolveRegisteredElement(trimmedName, slotId, elements, forms, pagesData)
      if (!ref) {
        return { ok: false, error: 'FIELD_NOT_FOUND', fieldName: trimmedName, slotId }
      }

      try {
        deps.setValue({
          formId: ref.formId,
          panelId: ref.panelId,
          elementId: ref.elementId,
          value: { [trimmedName]: value },
        })

        if (options?.refill !== false) {
          const typed = toTypedFieldValue(value)
          deps.refillElement(trimmedName, typed, ref.formId)
        }

        return { ok: true, fieldName: trimmedName, slotId, value: toPlainFieldValue(value) }
      } catch {
        return { ok: false, error: 'SET_FIELD_FAILED', fieldName: trimmedName, slotId }
      }
    },

    clearField(
      fieldName: string,
      options?: PlatformTestSetFieldOptions,
    ): PlatformTestApiResult {
      const slotId = resolveSlotId(deps, options)
      const trimmedName = String(fieldName ?? '').trim()
      if (!slotId) {
        return { ok: false, error: 'NO_ACTIVE_SLOT', fieldName: trimmedName }
      }

      const elements = deps.getElements()
      const forms = deps.getForms()
      const pagesData = deps.getPagesData()
      const ref = resolveRegisteredElement(trimmedName, slotId, elements, forms, pagesData)
      if (!ref) {
        return { ok: false, error: 'FIELD_NOT_FOUND', fieldName: trimmedName, slotId }
      }

      const element =
        (Object.values(elements).find((el: any) => {
          if (!el || typeof el !== 'object') return false
          const name = String(el.fieldName ?? '').trim() || trimmedName
          return (
            (name === trimmedName || el === elements[trimmedName]) &&
            String(el.formId ?? '') === ref.formId &&
            String(el.panelId ?? '') === ref.panelId &&
            String(el.elementId ?? '') === ref.elementId
          )
        }) as any) ?? (elements[trimmedName] as any)

      const emptyValue = resolveEmptyFieldValue(element?.elementType ?? element?.type)

      try {
        if (element && typeof element.empty === 'function') {
          element.empty()
        } else {
          deps.setValue({
            formId: ref.formId,
            panelId: ref.panelId,
            elementId: ref.elementId,
            value: { [trimmedName]: emptyValue },
          })
        }

        if (options?.refill !== false) {
          const typed =
            emptyValue != null &&
            typeof emptyValue === 'object' &&
            !Array.isArray(emptyValue) &&
            'type' in emptyValue &&
            'value' in emptyValue
              ? emptyValue
              : toTypedFieldValue(emptyValue)
          deps.refillElement(trimmedName, typed, ref.formId)
        }

        return {
          ok: true,
          fieldName: trimmedName,
          slotId,
          value: toPlainFieldValue(emptyValue),
        }
      } catch {
        return { ok: false, error: 'CLEAR_FIELD_FAILED', fieldName: trimmedName, slotId }
      }
    },

    waitForReady(timeoutMs = 10_000): Promise<PlatformTestApiResult> {
      const deadline = Date.now() + Math.max(0, timeoutMs)
      return new Promise((resolve) => {
        const tick = () => {
          if (this.isReady()) {
            resolve({ ok: true, slotId: this.getActiveSlot() })
            return
          }
          if (Date.now() >= deadline) {
            resolve({ ok: false, error: 'TIMEOUT' })
            return
          }
          setTimeout(tick, 50)
        }
        tick()
      })
    },
  }
}

export type PlatformTestApi = ReturnType<typeof createPlatformTestApi>
